<template>
  <div>
    <div>
      <v-layout row wrap>
        <v-flex d-flex md12>
          <Toolbar>
            <v-flex sm7 text-sm-center justify-end align-center class="bind">
              <div class="space-between">
                <Toggle
                  v-if="!isCurrentDay"
                  :modelValue="zmuLiveAvg"
                  :theme="theme"
                  @toggleValueChange="liveAvgToggleHandler"
                >
                  <v-btn flat value="live">{{ $t("Live") }}</v-btn>
                  <v-btn flat value="avg">{{ $t("Avg") }}</v-btn>
                </Toggle>
                <v-btn flat v-else>{{ $t("Avg") }}</v-btn>
              </div>
              <div class="space-between">
                <Toggle
                  :modelValue="paramCurPow"
                  :theme="theme"
                  @toggleValueChange="currentPowerToggleHandler"
                >
                  <v-btn flat value="dc_power">{{ $t("Power") }}</v-btn>
                  <v-btn flat value="dc_current">{{ $t("Current") }}</v-btn>
                </Toggle>
              </div>
              <div v-if="showRawComputedToggle" class="space-between">
                <Toggle
                  :modelValue="paramRawComp"
                  :theme="theme"
                  @toggleValueChange="rawComputedToggleHandler"
                >
                  <v-btn flat value="computed">{{ $t("Computed") }}</v-btn>
                  <v-btn flat value="raw">{{ $t("Raw") }}</v-btn>
                </Toggle>
              </div>
            </v-flex>
            <v-flex sm5 text-sm-right>
              <div class="body-1">
                {{ show_date }}
              </div>
            </v-flex>
          </Toolbar>
        </v-flex>
        <v-flex d-flex md7>
          <MapLegend
            v-for="leg in legend"
            :key="leg.name"
            :text="$t(leg.name)"
            :shape="leg.attr"
          />
        </v-flex>
        <v-flex xs12>
          <Card class="tablediv">
            <v-layout
              row
              wrap
              v-for="(item, idx) in zmuData"
              :key="idx"
              class="card-separation"
            >
              <v-flex md12 class="scb-header-data">
                <v-flex d-flex md12 lg12 xl12 class="zone-header">
                  <div class="checkbox">
                    <v-checkbox
                      v-model="checkbox[`${item.id}`]"
                      :label="item.name"
                      @change="zmuSelected(item.id)"
                    >
                    </v-checkbox>
                    <div class="vlt_header">
                      {{
                        paramCurPow === "dc_current"
                          ? `${$t("Total Current")} (A):`
                          : `${$t("Total Power")} (kW)`
                      }}<span class="scb_data"
                        >{{
                          paramCurPow === "dc_power"
                            ? item.total_zmu_power !== null
                              ? parseFloat(item.total_zmu_power).toFixed(2)
                              : $t("No Data")
                            : item.total_zmu_current !== null
                            ? parseFloat(item.total_zmu_current).toFixed(2)
                            : $t("No Data")
                        }}
                      </span>
                    </div>
                    <div class="temperature_header" v-if="item.isTemp">
                      {{ $t("Temp") }} (°C):<span class="scb_data"
                        >{{
                          item.temperature
                            ? parseFloat(item.temperature).toFixed(2)
                            : item.temperature !== null
                            ? 0
                            : $t("No Data")
                        }}
                      </span>
                    </div>
                    <div class="vlt_header" v-if="item.isVoltage">
                      {{ $t("Voltage") }} (V):<span class="scb_data"
                        >{{
                          item.voltage
                            ? parseFloat(item.voltage).toFixed(2)
                            : item.voltage !== null
                            ? 0
                            : $t("No Data")
                        }}
                      </span>
                    </div>
                  </div>
                </v-flex>
                <v-flex md12 lg12 xl12 class="scb-channel-data">
                  <v-flex pa-0 class="bind">
                    <v-checkbox
                      class="select_checkbox mt-0 pt-0"
                      v-model="channelCheckbox[item.id]"
                      :label="$t('Select All Channels')"
                      @change="channelSelected(item)"
                    ></v-checkbox>
                    <div class="tooltip" v-if="item.ref_id">
                      ( {{ $t("Ch Ref Id") }}: {{ item.ref_id }} )<span
                        class="tooltiptext"
                        >{{
                          $t("Channel Ref. Id for Underperforming String")
                        }}</span
                      >
                    </div>
                  </v-flex>
                  <CardCheckBox
                    :data="item.channel"
                    :id="item.id"
                    :clearAll="clearAllChecked"
                    :selectAll="channelCheckbox[item.id] || checkbox[item.id]"
                    :condition="cardCondition"
                    :hoverable="paramRawComp === 'computed'"
                    :onClick="onChannelClick"
                  >
                    <template v-slot:tooltip="props">
                      <p
                        v-if="paramRawComp === 'computed'"
                        :style="{ textAlign: 'center' }"
                      >
                        {{
                          props.value.multiplying_factor !== undefined
                            ? `${$t("Multiplication Factor")}: ${roundOff(
                                props.value.multiplying_factor
                              )}`
                            : `${$t("Multiplication Factor")} N/A`
                        }}
                      </p>
                    </template>
                  </CardCheckBox>
                </v-flex>
              </v-flex>
              <v-flex xs12>
                <v-divider></v-divider>
              </v-flex>
            </v-layout>
          </Card>
        </v-flex>
      </v-layout>

      <div class="button-align">
        <v-btn class="info" @click="showGraph()">{{ $t("Show Graph") }}</v-btn>
        <v-btn class="info" @click="clearAll()">{{ $t("Clear All") }}</v-btn>
      </div>
    </div>
    <GlobalGraph
      v-if="graphActive"
      class="graph"
      :series="series"
      :xAxisData="xAxis"
      :yAxis="yAxis"
      :theme="theme"
    />
  </div>
</template>

<script>
const momentTz = require('moment-timezone')
import moment from "moment";
import MapLegend from "@/components/common/MapLegend";
import { mapGetters, mapMutations } from "vuex";
import CommonService from "@/services/Common";
import ZmuService from "@/services/zmu";
import { mapGettersMixin } from "@/util/mixin/index";

export default {
  components: {
    MapLegend,
  },
  mixins: [mapGettersMixin],
  props: {
    changesFromFilter: {
      type: Object,
      default: function () {
        return {};
      },
    },
  },
  data() {
    return {
      showToolTip: false,
      activeChannels: {},
      checkbox: {},
      channelCheckbox: {},
      disableChannelSelection: false,
      clearAllChecked: false,
      graphActive: false,
      paramCurPow: "dc_current",
      paramRawComp: "raw",
      zmuLiveAvg: "live",
      zmuData: [],
      series: [],
      xAxis: [],
      yAxis: [
        {
          name: "Current (A)",
          axisLabel: {
            formatter: "{value}",
            rotate: 40,
          },
        },
      ],
      legend: [
        {
          name: "Known Issue",
          attr: {
            name: "square",
            color: "#ECB208",
          },
        },
        {
          name: "Under Performing",
          attr: {
            name: "square",
            color: "#FF5252",
          },
        },
        {
          name: "Comm Error",
          attr: {
            name: "square",
            color: "#F69946",
          },
        },
        {
          name: "NC",
          attr: {
            name: "square",
            color: "#455A64",
          },
        },
        {
          name: "Selected",
          attr: {
            name: "square",
            color: "#818fff",
          },
        },
      ],
      isZone: [],
      isWatchEnabled: false,
      blockList: [],
      activeFilter: {}
    };
  },
  computed: {
    ...mapGetters(["getSelectedPlantId", "getActivePlantTimezone", "getRules"]),
    filter() {
      return [
        {
          id: "Date",
          type: "singleDatePicker",
          title: "Date",
        },
        {
          id: "blockList",
          type: "select",
          title: "Select Block",
          value: this.blockList
        },
      ]
    },
    full_date() {
      return this.activeFilter && this.activeFilter.Date
        ? this.activeFilter.Date.from
        : momentTz().tz(this.getActivePlantTimezone).format("YYYY-MM-DD");
    },
    selectedId() {
      return this.activeFilter && this.activeFilter.blockList
        ? this.activeFilter.blockList.id
        : null;
    },
    combinedWatch() {
      return `${this.full_date}&${this.selectedId}`;
    },
    // TODO: Remove this when the time of refactoring API
    show_date() {
      return moment(this.full_date).format("YYYY/M/D");
    },
    showRawComputedToggle() {
      return this.getRules(["rule", "comp_config", "zmu"]) &&
        this.getRules(["rule", "comp_config", "zmu"])["options"] &&
        this.getRules(["rule", "comp_config", "zmu"])["options"]["isComputed"]
        ? this.getRules(["rule", "comp_config", "zmu"])["options"]["isComputed"]
        : false;
    },
    isCurrentDay() {
      const currentDate = momentTz().tz(this.getActivePlantTimezone).format("YYYY-MM-DD");
      let date = moment(this.full_date).format("YYYY-MM-DD");
      return date !== currentDate;
    },
  },
  async mounted() {
    await this.initialConfigList();
    await this.setIntervalWithoutDelay(this.callAllApi);
  },
  methods: {
    ...mapMutations([
      "SET_PROGRESS_BAR",
      "SET_SHOW_SNACKBAR",
      "SET_SNACKBAR_CONTENT"
    ]),
    roundOff(numb) {
      return Math.round((numb + Number.EPSILON) * 100) / 100;
    },
    cardCondition(ch, isActive) {
      const currentTime = momentTz().tz(this.getActivePlantTimezone).format("HH");
      let active = !!isActive;
      const isRedBlink =
        parseFloat(ch.value) === 0.0 &&
        this.scbLiveAvg === "live" &&
        (ch.status === "no_issue" || ch.status === "string_underperforming");
      const isWithinGenerationTime = currentTime > 8 && currentTime < 18;

      if (isRedBlink && isWithinGenerationTime && active) {
        return "ch-value_selected";
      }

      if (
        isRedBlink &&
        isWithinGenerationTime &&
        !active &&
        ch.status !== "known_issue"
      ) {
        return "ch-value";
      }

      if (isRedBlink && isWithinGenerationTime && !active) {
        return "default";
      }

      if (!(isRedBlink && isWithinGenerationTime) && active) {
        return "default-select";
      }

      if (!(isRedBlink && isWithinGenerationTime) && !active) {
        return "default";
      }
    },
    onChannelClick(ch, isActive, id) {
      const identifier = `${id}-${ch.id}`;
      if (ch.status !== "not_connected") {
        let state = !isActive[identifier];
        this.$set(isActive, identifier, state);

        this.$set(this.activeChannels, identifier, state);
      } else {
        let payload = {
          icon: "error",
          header: "Important !",
          message: "Not-Connected channel cannot be selected",
        };
        this.SET_SNACKBAR_CONTENT(payload);
        this.SET_SHOW_SNACKBAR(true);
      }
    },
    async initialConfigList() {
      let response = await this.getBlockList();
      this.blockList = response.data.message

      const Obj = {}
      const cloneFilterOption = _.cloneDeep(this.filter);
      cloneFilterOption.forEach(child => {
        if ((child.condition && (!!child.considerStateAlways || child.condition(Obj[child.ref]))) || !child.condition) {
          if (child.type === 'select') {
            if (typeof child.value === 'function') {
              Obj[child.id] = child.value(Obj[child.ref])
            } else {
              Obj[child.id] = child.value[0]
            }
          } else if (child.type === 'singleDatePicker') {
            let parameters = ['from', 'to']
            parameters.forEach(key => {
              if (!Obj[child.id]) { Obj[child.id] = {} }
              Obj[child.id][key] = momentTz().tz(this.getActivePlantTimezone).format('YYYY-MM-DD')
            })
          } else {
            Obj[child.id] = child.value
          }
        }
      })
      this.activeFilter = Obj;
      this.$emit("filter:config", cloneFilterOption);
      this.$emit('initial:active_filter', Obj)
    },
    async callAllApi() {
      this.enableWatch = false;
      await this.getZmuTableData();
      this.graphActive = false;
    },
    updateSnackbar(message) {
      let payload = {
        icon: "error",
        message,
      };
      this.SET_SNACKBAR_CONTENT(payload);
      this.SET_SHOW_SNACKBAR(true);
    },
    async liveAvgToggleHandler(val) {
      this.zmuLiveAvg = val;
      await this.setIntervalWithoutDelay(this.getZmuRefreshData);
    },
    currentPowerToggleHandler(val) {
      this.paramCurPow = val;
      this.getZmuTableData();
      this.graphActive = false;
    },
    rawComputedToggleHandler(val) {
      this.paramRawComp = val;
      this.getZmuTableData();
      this.graphActive = false;
    },
    async getZmuTableData() {
      try {
        this.isWatchEnabled = true;
        await this.getZmuRefreshData();

        Object.entries(this.zmuData).forEach(([key, value]) => {
          this.$set(this.checkbox, key, false);
          this.$set(this.channelCheckbox, key, false);
          let channels = value.channel;
          if (channels !== undefined) {
            channels.forEach((el) => {
              if (el.status !== "not_connected") {
                this.activeChannels[`${key}-${el.id}`] = false;
              }
            });
          }
        });
      } catch (err) {
        this.SET_PROGRESS_BAR(false);
      }
    },
    async getZmuRefreshData() {
      this.SET_PROGRESS_BAR(true);
      const param = {
        plantId: this.getSelectedPlantId,
        index: this.zmuLiveAvg,
        blockId: this.selectedId,
        date: moment(this.full_date).format("YYYY-MM-DD"),
      };
      const response = await ZmuService.fetchZmuTableData(
        param,
        this.paramRawComp === "computed"
          ? `computed_${this.paramCurPow}`
          : this.paramCurPow
      );
      this.zmuData = response.data;
      this.SET_PROGRESS_BAR(false);
    },
    async getZmuGraphData(modifiedId) {
      try {
        this.SET_PROGRESS_BAR(true);
        const param = {
          plantId: this.getSelectedPlantId,
          modifiedId: modifiedId,
          date: moment(this.full_date).format("YYYY-MM-DD"),
          isZone: this.isZone,
        };
        const response = await ZmuService.fetchZmuGraphData(
          param,
          this.paramRawComp === "computed"
            ? `computed_${this.paramCurPow}`
            : this.paramCurPow
        );
        this.SET_PROGRESS_BAR(false);
        this.series.length = 0;
        Object.keys(response.data.finalData).forEach((id) => {
          this.series.push({
            name: id,
            type: "line",
            data: response.data.finalData[id],
          });
        });
        this.xAxis = response.data.axis;
        this.graphActive = true;
      } catch (err) {
        this.SET_PROGRESS_BAR(false);
      }
    },
    async getBlockList() {
      return CommonService.getIdList(this.getSelectedPlantId, "zmu", 1, null);
    },
    zmuSelected(zmuId) {
      if (!this.isZone.includes(zmuId)) {
        this.isZone.push(zmuId);
      } else {
        let index = this.isZone.indexOf(zmuId);
        if (index > -1) {
          this.isZone.splice(index, 1);
        }
      }

      let isActiveKeys = Object.keys(this.activeChannels).map((el) => {
        let id = el.slice(0, 8);
        if (id[7] === "-") {
          id = id.slice(0, 7);
        }
        if (id === zmuId) {
          return el;
        }
      });
      isActiveKeys.forEach((el) => {
        if (el !== undefined) {
          delete this.activeChannels[el];
          this.$set(this.activeChannels, el, this.checkbox[zmuId]);
        }
      });
      let selectedChannels = Object.keys(this.activeChannels).filter((el) => {
        if (this.activeChannels[el]) {
          return el;
        }
      });
      if (selectedChannels.length === 0) {
        this.graphActive = false;
      }
    },
    channelSelected(val) {
      if (this.channelCheckbox[val.id]) {
        val.channel.forEach((el) => {
          if (el.status !== "not_connected") {
            let id = `${val.id}-${el.id}`;
            this.activeChannels[id] = true;
          }
        });
      } else {
        val.channel.forEach((el) => {
          if (el.status !== "not_connected") {
            let id = `${val.id}-${el.id}`;
            this.activeChannels[id] = false;
          }
        });
      }
    },
    async showGraph() {
      let selectedChannels = Object.keys(this.activeChannels).filter((el) => {
        if (this.activeChannels[el]) {
          return el;
        }
      });
      let modifiedId = {};
      if (selectedChannels.length !== 0) {
        selectedChannels.forEach((el) => {
          let id = el.slice(0, 8);
          if (id[7] === "-") {
            id = id.slice(0, 7);
          }
          let chId = el.slice(-2).replace("-", "");
          if (!modifiedId.hasOwnProperty(id)) {
            modifiedId[id] = [];
            modifiedId[id].push(chId);
          } else {
            modifiedId[id].push(chId);
          }
        });
        await this.getZmuGraphData(modifiedId);
        const el = document.querySelector(".graph");
        if (el) {
          el.scrollIntoView();
        }
      } else {
        this.updateSnackbar("Please Select Channel/ZMU");
      }
    },
    clearAll() {
      this.activeChannels = {};
      this.channelCheckbox = {};

      // Removing the border from scb checkbox container if its there
      this.disableChannelSelection = false;
      let eleArr = document.querySelectorAll(`.scb-header-data`);
      eleArr.forEach((el) => {
        if (el.classList.contains("scb-selected")) {
          el.classList.remove("scb-selected");
        }
      });
      this.clearAllChecked = !this.clearAllChecked;
      this.graphActive = false;
    },
  },
  watch: {
    changesFromFilter: {
      handler(val) {
        this.activeFilter = val;
      },
      deep: true,
    },
    async getSelectedPlantId() {
      this.channelCheckbox = {};
      this.activeChannels = {};
      this.graphActive = false;
      await this.initialConfigList();
      await this.setIntervalWithoutDelay(this.callAllApi);
    },
    isReload() {
      if (this.isReload) {
        this.setIntervalWithoutDelay(this.getZmuRefreshData);
        if (this.graphActive) {
          this.showGraph();
        }
      }
    },
    combinedWatch(newVal, oldVal) {
      const newValSplitArr = newVal.split("&");
      const oldValSplitArr = oldVal.split("&");
      const currentDate = momentTz().tz(this.getActivePlantTimezone).format("YYYY-MM-DD");
      let date = moment(this.full_date).format("YYYY-MM-DD");

      if (date !== currentDate) {
        this.zmuLiveAvg = "avg";
      } else {
        this.zmuLiveAvg = "live";
      }
      if (newValSplitArr[1] != oldValSplitArr[1]) {
        if (this.isWatchEnabled) {
          this.callAllApi();
          return;
        }
      }
      if (newValSplitArr[0] != oldValSplitArr[0]) {
        this.setIntervalWithoutDelay(this.getZmuRefreshData);
        if (this.graphActive) {
          this.showGraph();
        }
      }
    },
  },
};
</script>

<style scoped>
.space-between {
  margin-right: 4px;
}

.bind {
  display: inline-flex;
}

.select_checkbox {
  margin-left: 15px !important;
  margin-bottom: 0px !important;
}

.zone-header {
  padding: 0px;
}

.graph {
  width: 100%;
  height: 400px;
}

.scb_data {
  /* color: white; */
  font-size: 16px;
  padding: 2px;
}

.theme--light .scb_data {
  /* color: black; */
  font-size: 16px;
}

.button-align {
  text-align: center;
  padding-top: 1px;
}

.vlt_header {
  /* color: #818fff; */
  font-weight: bold;
  font-size: 14px;
  margin-top: 7px;
  margin-right: 8px;
}

.temperature_header {
  /* color: #818fff; */
  font-weight: bold;
  font-size: 14px;
  margin-top: 7px;
  margin-right: 10px;
}

.checkbox {
  padding-left: 15px;
  display: inline-flex;
}

.theme--dark .tooltip {
  margin-top: 2.5px;
  margin-left: 3px;
  /* color: #909cff; */
  font-size: 12px;
  position: relative;
  display: inline-block;
  font-weight: bold;
}

.theme--light .tooltip {
  margin-top: 2.5px;
  margin-left: 3px;
  /* color: #3347df; */
  font-size: 12px;
  position: relative;
  display: inline-block;
  font-weight: bold;
}

.theme--dark .tooltip .tooltiptext {
  visibility: hidden;
  width: 255px;
  /* background-color: #F5F5F5; */
  /* color: black; */
  text-align: center;
  border-radius: 6px;
  padding: 2px 0;

  /* Position the tooltip */
  position: absolute;
  z-index: 1;
  bottom: 100%;
  left: 50%;
  margin-left: -60px;
}

.theme--light .tooltip .tooltiptext {
  visibility: hidden;
  width: 255px;
  /* background-color: #e3e3e3; */
  color: black;
  text-align: center;
  border-radius: 6px;
  padding: 2px 0;

  /* Position the tooltip */
  position: absolute;
  z-index: 1;
  bottom: 100%;
  left: 50%;
  margin-left: -60px;
}

.tooltip:hover .tooltiptext {
  visibility: visible;
}

@media only screen and (min-width: 1904px) and (max-width: 2544px) {
  .tablediv {
    max-height: 580px;
    min-height: 540px;
    overflow-y: auto;
    overflow-x: hidden;
    margin: 4px;
  }
}

@media only screen and (min-width: 1584px) and (max-width: 1904px) {
  .tablediv {
    max-height: 430px;
    min-height: 390px;
    overflow-y: auto;
    overflow-x: hidden;
    margin: 4px;
  }
}

@media only screen and (min-width: 1394px) and (max-width: 1585px) {
  .tablediv {
    max-height: 340px;
    min-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
    margin: 4px;
  }
}

@media only screen and (min-width: 1280px) and (max-width: 1394px) {
  .tablediv {
    max-height: 270px;
    min-height: 230px;
    overflow-y: auto;
    overflow-x: hidden;
    margin: 4px;
  }
}
</style>
